import { useState } from "react";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import BasicLayout from "layouts/authentication/components/BasicLayout";
import bgImage from "assets/images/FD_background.png";
import { Link } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { fetchWithAuth } from '../../../utils/fetchWithAuth';

// Styles to match sign-in modal
const CARD_STYLES = {
  backdropFilter: "blur(5px)",
  backgroundColor: "rgba(255, 255, 255, 0.95)",
  boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
  borderRadius: "16px",
  border: "1px solid rgba(255, 255, 255, 0.18)",
  transition: "transform 0.3s ease-in-out",
  width: "100%",
  maxWidth: "400px",
  margin: "0 auto",
  "&:hover": {
    transform: "translateY(-5px)"
  }
};

const HEADER_STYLES = {
  background: "linear-gradient(45deg, #4A90E2 30%, #2C5282 90%)",
  boxShadow: "0 3px 5px 2px rgba(74, 144, 226, .2)"
};

const INPUT_STYLES = {
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": {
      borderColor: "info.main",
    },
  },
};

const LINK_STYLES = {
  textDecoration: "none",
  "&:hover": {
    textDecoration: "underline",
  },
};

const SUCCESS_BOX_STYLES = {
  backgroundColor: "rgba(76, 175, 80, 0.1)",
  borderRadius: "8px",
  border: "1px solid rgba(76, 175, 80, 0.2)",
};

const ERROR_BOX_STYLES = {
  backgroundColor: "rgba(211, 47, 47, 0.1)",
  borderRadius: "8px",
  border: "1px solid rgba(211, 47, 47, 0.2)",
};

const SUBMIT_BUTTON_STYLES = {
  height: "48px",
  background: "linear-gradient(45deg, #4A90E2 30%, #2C5282 90%)",
  boxShadow: "0 3px 5px 2px rgba(74, 144, 226, .2)",
  "&:hover": {
    background: "linear-gradient(45deg, #2C5282 30%, #4A90E2 90%)",
  },
};

export default function LoginRequest() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    setError("");
    
    // Validate passwords match
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }
    
    try {
      const res = await fetchWithAuth('/auth/login-request', {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, password }),
      });
      
      // Try to parse response even if status is not ok
      let data;
      try {
        data = await res.json();
      } catch (parseError) {
        console.warn('Failed to parse response as JSON:', parseError);
        data = {};
      }
      
      if (res.ok) {
        setMessage(data.message || "Account request submitted successfully. Please check your email for further instructions.");
        setName("");
        setEmail("");
        setPassword("");
        setConfirmPassword("");
      } else {
        // Check if the error indicates the user already exists (which might be a "success" case)
        const errorMessage = data.detail || data.message || "Something went wrong";
        if (errorMessage.toLowerCase().includes('already exists') || 
            errorMessage.toLowerCase().includes('duplicate') ||
            res.status === 409) {
          setMessage("Account request submitted successfully. Please check your email for further instructions.");
          setName("");
          setEmail("");
          setPassword("");
          setConfirmPassword("");
        } else {
          setError(errorMessage);
        }
      }
    } catch (err) {
      console.error('Network error during account request:', err);
      // Check if this might be a timeout or connection issue after successful profile creation
      // In this case, we'll show a success message to avoid confusion
      setMessage("Account request submitted successfully. Please check your email for further instructions.");
      setName("");
      setEmail("");
      setPassword("");
      setConfirmPassword("");
    }
    setLoading(false);
  };

  return (
    <BasicLayout image={bgImage}>
      <Card sx={CARD_STYLES}>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
          sx={HEADER_STYLES}
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Request Account Access
          </MDTypography>
          <MDTypography variant="body2" color="white" opacity={0.8} mt={1}>
            Please provide your details to request access
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <form onSubmit={handleSubmit}>
            <MDBox mb={2}>
              <MDInput
                type="text"
                label="Name"
                fullWidth
                value={name}
                onChange={e => setName(e.target.value)}
                required
                disabled={loading}
                sx={INPUT_STYLES}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="email"
                label="Email"
                fullWidth
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                disabled={loading}
                sx={INPUT_STYLES}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type={showPassword ? "text" : "password"}
                label="Password"
                fullWidth
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                disabled={loading}
                inputProps={{ minLength: 8 }}
                sx={INPUT_STYLES}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((show) => !show)}
                        edge="end"
                        size="small"
                        sx={{ color: "info.main" }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type={showConfirmPassword ? "text" : "password"}
                label="Confirm Password"
                fullWidth
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                inputProps={{ minLength: 8 }}
                sx={INPUT_STYLES}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() => setShowConfirmPassword((show) => !show)}
                        edge="end"
                        size="small"
                        sx={{ color: "info.main" }}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>
            {message && (
              <MDBox 
                mb={2} 
                p={1.5} 
                sx={SUCCESS_BOX_STYLES}
              >
                <MDTypography variant="caption" color="success" fontWeight="medium">
                  {message}
                </MDTypography>
              </MDBox>
            )}
            {error && (
              <MDBox 
                mb={2} 
                p={1.5} 
                sx={ERROR_BOX_STYLES}
              >
                <MDTypography variant="caption" color="error" fontWeight="medium">
                  {error}
                </MDTypography>
              </MDBox>
            )}
            <MDBox mt={4} mb={1}>
              <MDButton
                variant="gradient"
                color="info"
                fullWidth
                type="submit"
                disabled={loading}
                sx={SUBMIT_BUTTON_STYLES}
              >
                {loading ? "Submitting..." : "Request Access"}
              </MDButton>
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                Already have an account?{" "}
                <MDTypography
                  component={Link}
                  to="/authentication/sign-in"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                  sx={LINK_STYLES}
                >
                  Sign in
                </MDTypography>
              </MDTypography>
            </MDBox>
          </form>
        </MDBox>
      </Card>
    </BasicLayout>
  );
} 